import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Bell, Save, ChevronDown, ChevronRight } from "lucide-react";

const LEAD_LINKED = [
  { key: "partner", label: "Partner / DSA (on lead)" },
  { key: "manager", label: "Sales Manager (assigned to lead)" },
];
const BUILTIN_ROLES = [
  { key: "admin", label: "Admin" },
  { key: "sales_manager", label: "Sales Manager" },
  { key: "master_dsa", label: "Master DSA" },
  { key: "dsa", label: "DSA" },
  { key: "agent", label: "Agent" },
];
const PLACEHOLDERS = ["{customer_name}", "{stage}", "{loan_type}", "{amount}", "{partner_name}", "{lender_name}", "{lead_id}", "{manager_name}"];

export default function EmailNotifications() {
  const [rules, setRules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState("");

  const load = useCallback(() => {
    api.get("/notification-rules").then((r) => setRules(r.data)).catch(() => setRules([]));
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => setRoles([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleOptions = [...BUILTIN_ROLES, ...roles.filter((r) => !BUILTIN_ROLES.some((b) => b.key === r.key))];

  const patch = (stage, p) => setRules((rs) => rs.map((r) => (r.stage === stage ? { ...r, ...p } : r)));
  const toggleRecipient = (stage, key) => setRules((rs) => rs.map((r) => {
    if (r.stage !== stage) return r;
    const has = r.recipients.includes(key);
    return { ...r, recipients: has ? r.recipients.filter((x) => x !== key) : [...r.recipients, key] };
  }));

  const save = async (r) => {
    setSaving(r.stage);
    try {
      await api.put(`/notification-rules/${encodeURIComponent(r.stage)}`, { enabled: r.enabled, recipients: r.recipients, subject: r.subject, body: r.body });
      toast.success(`Saved notification rule for "${r.stage}"`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving("");
  };

  const resetRule = async (r) => {
    try {
      await api.delete(`/notification-rules/${encodeURIComponent(r.stage)}`);
      toast.success(`"${r.stage}" reset to defaults`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="space-y-6" data-testid="email-notifications-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Email Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Send an email automatically when a lead reaches a stage. {enabledCount} of {rules.length} stages enabled.</p>
      </div>

      <div className="space-y-3">
        {rules.map((r) => (
          <div key={r.stage} data-testid={`rule-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <button data-testid={`rule-toggle-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setOpen(open === r.stage ? null : r.stage)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                  {open === r.stage ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{r.stage}</div>
                  <div className="text-xs text-slate-400 truncate">{r.recipients.length ? `Notifies ${r.recipients.length} recipient group(s)` : "No recipients selected"}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <span className={`text-xs font-medium ${r.enabled ? "text-emerald-600" : "text-slate-400"}`}>{r.enabled ? "On" : "Off"}</span>
                <button type="button" data-testid={`rule-enable-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => patch(r.stage, { enabled: !r.enabled })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${r.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${r.enabled ? "translate-x-5" : ""}`} />
                </button>
              </label>
            </div>

            {open === r.stage && (
              <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/50">
                <div>
                  <label className={labelCls}>Recipients (who gets the email)</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {LEAD_LINKED.map((o) => (
                      <button key={o.key} type="button" data-testid={`recipient-${r.stage.replace(/\s+/g, "-").toLowerCase()}-${o.key}`} onClick={() => toggleRecipient(r.stage, o.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${r.recipients.includes(o.key) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        {o.label}
                      </button>
                    ))}
                    {roleOptions.map((o) => (
                      <button key={o.key} type="button" data-testid={`recipient-${r.stage.replace(/\s+/g, "-").toLowerCase()}-role-${o.key}`} onClick={() => toggleRecipient(r.stage, o.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${r.recipients.includes(o.key) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        All {o.label}s
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Subject</label>
                  <input data-testid={`rule-subject-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} value={r.subject} onChange={(e) => patch(r.stage, { subject: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Body</label>
                  <textarea data-testid={`rule-body-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} rows={6} value={r.body} onChange={(e) => patch(r.stage, { body: e.target.value })} className={`${inputCls} font-mono text-xs`} />
                </div>
                <div className="text-xs text-slate-400">
                  Placeholders: {PLACEHOLDERS.map((p) => <code key={p} className="bg-slate-100 px-1 rounded mr-1">{p}</code>)}
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" data-testid={`rule-reset-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => resetRule(r)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Reset to defaults</button>
                  <button data-testid={`rule-save-${r.stage.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => save(r)} disabled={saving === r.stage} className={btnPrimary}>
                    <Save size={15} /> {saving === r.stage ? "Saving…" : "Save Rule"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
